import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { RegisterFormData, ApiError } from '../types/authTypes';
import api from '../api/axiosConfig';
import GoogleSignInButton from './GoogleSignInButton';
import { afterAuthPath, nextFromLocation } from '../utils/returnTo';
import { usePublicLanguage } from '../contexts/PublicLanguageContext';
import './RegisterForm.css';

const RegisterForm: React.FC = () => {
  const { t } = usePublicLanguage();
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    registrationCode: ''
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
      document.body.classList.add('register-page');
      return () => {
        document.body.classList.remove('register-page');
      };
    }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    //console.log("API Base URL:", process.env.REACT_APP_API_BASE_URL);
    // Client-side validation
    if (!formData.email.includes('@')) {
      setError(t.auth.invalidEmail);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t.auth.passwordsDoNotMatch);
      return;
    }

    if (formData.password.length < 8) {
      setError(t.auth.passwordTooShort);
      return;
    }

    try {
      setIsLoading(true);
      // No need to get CSRF token first
      const response = await api.post('/auth/register/', {
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      });
      
      if (response.status === 201) {
        // Keep ?next= across the hop: someone who came from an invite link
        // should land back on it once they have signed in.
        const next = nextFromLocation();
        navigate(next ? `/login?next=${encodeURIComponent(next)}` : '/login',
          { state: { registrationSuccess: true } });
      }
    } catch (err) {
      const error = err as AxiosError<ApiError>;
      if (error.response) {
        const data = error.response.data as ApiError;
        
        if (data.email) {
          const message = Array.isArray(data.email) 
            ? data.email.join(' ') 
            : data.email;
          setError(t.auth.emailErrorPrefix.replace('{message}', message));
        } else if (data.password) {
          const message = Array.isArray(data.password) 
            ? data.password.join(' ') 
            : data.password;
          setError(t.auth.passwordErrorPrefix.replace('{message}', message));
        } else if (data.non_field_errors) {
          const message = Array.isArray(data.non_field_errors) 
            ? data.non_field_errors.join(' ') 
            : data.non_field_errors;
          setError(message);
        } else if (data.detail) {
          setError(data.detail);
        } else {
          setError(t.auth.registrationFailed);
        }
      }
      console.error("Full error object:", err);
      if (axios.isAxiosError(err)) {
        console.error("Error response data:", err.response?.data);
        console.error("Error status:", err.response?.status);
        console.error("Error headers:", err.response?.headers);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">
      <h2 className="register-title">{t.auth.registerTitle}</h2>
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            {t.auth.emailField}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="form-input"
            required
          />
        </div>
        
        {/* Optional username field */}
        {false && ( // Change to true if you want to collect username
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              {t.auth.usernameOptional}
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="form-input"
            />
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="password" className="form-label">
            {t.auth.passwordField}
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className="form-input"
            required
            minLength={8}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="confirmPassword" className="form-label">
            {t.auth.confirmPasswordField}
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className="form-input"
            required
            minLength={8}
          />
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className="submit-button"
        >
          {isLoading ? t.auth.registering : t.auth.registerButton}
        </button>
      </form>

      <GoogleSignInButton
        onSuccess={() => window.location.assign(afterAuthPath())}
        onError={(message) => setError(message)}
      />

      <div className="login-link-container">
        <span className="login-link-text">{t.auth.haveAccount}</span>
        <Link 
          to={`/login${window.location.search}`} 
          className="login-link"
        >
          {t.auth.loginHere}
        </Link>
      </div>
    </div>
  );
};

export default RegisterForm;